<template>
    <v-card elevation="7">
        <v-card-text class="px-0">
            <v-container fluid>
                <v-row>
                    <v-col class="py-0">
                        <scope-options-table
                            title="Scopes"
                            :headers="scopeHeaders"
                            :items="assignedScopes"
                            :selected-item-index="selectedScopeIndex"
                            :disable-actions="!allowEdit"
                            no-data-text="Please add at least one scope!"
                            @on-row-action-click="onDeleteScopeRow"
                            @on-table-row-select="onScopeSelectChange"
                            @on-row-with-grant-change="onScopeWithGrantChange"
                        ></scope-options-table>
                    </v-col>
                    <v-col
                        cols="1"
                        class="pa-0 text-center"
                        align-self="center"
                    >
                        <v-icon color="secondary" x-large>{{
                            icons.arrowForward
                        }}</v-icon>
                    </v-col>
                    <v-col class="py-0">
                        <scope-options-table
                            title="Roles"
                            :headers="roleHeaders"
                            :items="assignedRoles"
                            :selected-item-index="selectedRoleIndex"
                            :parent-with-grant="selectedAssignedScopeWithGrant"
                            :disable-actions="!allowEdit"
                            no-data-text="Please add at least one role!"
                            @on-row-action-click="onDeleteRoleRow"
                            @on-table-row-select="onRoleSelectChange"
                            @on-row-with-grant-change="onRoleWithGrantChange"
                        ></scope-options-table>
                    </v-col>
                </v-row>
                <v-row v-if="allowAdd">
                    <v-col class="pb-0 pt-1">
                        <v-sheet>
                            <v-select
                                v-model="selectedAssignableScope"
                                :items="filteredAssignableScopes"
                                label="Add a scope"
                                outlined
                                hide-details
                                clearable
                                class="mt-2 add-scope-options-select"
                                dense
                                eager
                                :suffix="`(${filteredAssignableScopes.length})`"
                                :disabled="!allowEdit"
                                @change="onAssignableScopesChange"
                            >
                            </v-select>
                        </v-sheet>
                    </v-col>
                    <v-col cols="1" class="py-0"> </v-col>
                    <v-col class="pb-0 pt-1">
                        <v-sheet>
                            <v-select
                                v-model="selectedAssignableRole"
                                :items="filteredAssignableRoles"
                                label="Add a role"
                                outlined
                                hide-details
                                clearable
                                class="mt-2 add-scope-options-select"
                                dense
                                eager
                                :suffix="`(${filteredAssignableRoles.length})`"
                                :disabled="
                                    !allowEdit ||
                                        !filteredAssignableRoles.length
                                "
                                @change="onAssignableRolesChange"
                            ></v-select>
                        </v-sheet>
                    </v-col>
                </v-row>
            </v-container>
        </v-card-text>
    </v-card>
</template>

<script>
import ScopeOptionsTable from './ScopeOptionsTable';
import userScopeMixin from '@/mixins/userScopeMixin';

export default {
    name: 'ScopeMasterDetailView',
    components: {
        'scope-options-table': ScopeOptionsTable
    },
    mixins: [userScopeMixin],
    props: {
        allowAdd: {
            type: Boolean,
            required: false,
            default: false
        },
        allowEdit: {
            type: Boolean,
            required: false,
            default: true
        },
        // the assigned scopes for the selected user
        scopesData: {
            type: Array,
            required: true
        },
        // scopes from logged in user
        grantableScopesData: {
            type: Array,
            required: true
        },
        // expanded scopes from logged in user
        assignableScopesData: {
            type: Array,
            required: true
        },
        xocAuth: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            scopeHeaders: [
                { text: 'Scope', type: 'text', value: 'scope' },
                { text: 'Grant', value: 'withGrant' },
                { text: 'Info', value: 'info' },
                { text: ' ', value: 'action' }
            ],
            roleHeaders: [
                { text: 'Role', type: 'text', value: 'role' },
                { text: 'Grant', value: 'withGrant' },
                { text: 'Info', value: 'info' },
                { text: ' ', value: 'action' }
            ],
            selectedScopeIndex: -1,
            selectedRoleIndex: -1,
            // this view has assigned scopes/roles listed in the data table and
            // assignable scopes/roles which is displayed in the add new dropdown
            assignedScopesAndRoles: [],
            selectedAssignableScope: null,
            selectedAssignableRole: null,
            assignableScopes: [],
            assignableRoles: []
        };
    },
    computed: {
        assignedScopes() {
            return this.assignedScopesAndRoles.map(item => {
                return {
                    name: item.name,
                    mso: item.mso,
                    partner: item.partner,
                    venue: item.venue,
                    withGrant: item.withGrant,
                    disableEdit: item.disableEdit,
                    info: item.roles.length,
                    error: item.roles.length
                        ? null
                        : 'Scope must have at least one role'
                };
            });
        },
        assignedRoles() {
            if (this.assignedScopesAndRoles.length) {
                return this.assignedScopesAndRoles[this.selectedScopeIndex]
                    .roles;
            } else {
                return [];
            }
        },
        selectedAssignedScopeWithGrant() {
            if (this.assignedScopes.length && this.selectedScopeIndex >= 0) {
                return this.assignedScopes[this.selectedScopeIndex].withGrant;
            } else {
                return false;
            }
        },
        filteredAssignableScopes() {
            // need to filterout the assigned scopes so they don't show on the dropdown
            let allItems = this.assignableScopes.map(scope => {
                return {
                    text: `${scope.mso} | ${scope.partner} | ${scope.venue}`,
                    value: scope,
                    disabled: this.isScopeSelectionDisabled(scope, this.xocAuth)
                };
            });
            if (this.assignedScopesAndRoles.length) {
                return allItems.filter(item => {
                    return !this.findScopeExactMatch(
                        item.value,
                        this.assignedScopesAndRoles
                    );
                });
            } else {
                return allItems;
            }
        },
        filteredAssignableRoles() {
            // need to filter out the assigned scopes
            const allItems = this.assignableRoles.map(role => {
                return {
                    text: role.name,
                    value: role
                };
            });
            if (this.assignedRoles.length) {
                return allItems.filter(item => {
                    return !this.assignedRoles.find(role => {
                        return role.name === item.value.name;
                    });
                });
            } else {
                return allItems;
            }
        },
        isScopesAndRolesValid() {
            if (this.assignedScopes.length) {
                return (
                    this.assignedScopes.filter(item => {
                        return !!item.error;
                    }).length <= 0
                );
            } else {
                return false;
            }
        }
    },
    watch: {
        selectedScopeIndex(value) {
            if (
                value >= 0 &&
                this.assignedScopesAndRoles.length &&
                this.assignedScopesAndRoles[value]
            ) {
                this.populateAssignableRoles(
                    this.assignedScopesAndRoles[value]
                );
            }
        },
        scopesData: {
            immediate: true,
            handler(value) {
                if (value) {
                    this.populateData();
                }
            }
        }
    },
    methods: {
        // ========================= Events ================================
        onDeleteScopeRow(scope) {
            if (this.unAssignScope(scope, this.assignedScopesAndRoles)) {
                this.notifyScopesChanged();
            }
            this.selectedScopeIndex = 0;
        },
        onDeleteRoleRow(role) {
            const parentScope = this.assignedScopesAndRoles[
                this.selectedScopeIndex
            ];
            if (this.unAssignRole(role, parentScope)) {
                this.notifyScopesChanged();
            }
            this.selectedRoleIndex = 0;
        },
        onScopeSelectChange(index) {
            this.selectedScopeIndex = index;
        },
        onRoleSelectChange(index) {
            this.selectedRoleIndex = index;
        },
        onScopeWithGrantChange(value) {
            // grant/ungrant scope
            if (
                this.assignedScopesAndRoles.length &&
                this.selectedScopeIndex >= 0
            ) {
                this.assignedScopesAndRoles[
                    this.selectedScopeIndex
                ].withGrant = value;
                this.notifyScopesChanged();
            }
        },
        onRoleWithGrantChange(value) {
            // grant/ungrant role
            if (
                this.assignedScopesAndRoles.length &&
                this.selectedScopeIndex >= 0
            ) {
                this.assignedScopesAndRoles[this.selectedScopeIndex].roles[
                    this.selectedRoleIndex
                ].withGrant = value;
                this.notifyScopesChanged();
            }
        },
        onAssignableScopesChange(value) {
            if (
                this.assignScope(
                    value,
                    this.assignedScopesAndRoles,
                    this.grantableScopesData
                )
            ) {
                this.selectedScopeIndex =
                    this.assignedScopesAndRoles.length - 1;
                this.notifyScopesChanged();
            }
            this.selectedAssignableScope = {};
        },
        onAssignableRolesChange(value) {
            const parentScope = this.assignedScopesAndRoles[
                this.selectedScopeIndex
            ];
            if (this.assignRole(value, parentScope, this.grantableScopesData)) {
                this.selectedRoleIndex = parentScope.roles.length - 1;
                this.notifyScopesChanged();
            }
            this.selectedAssignableRole = {};
        },
        // ===================== Other =============================
        populateData() {
            this.populateAssignedScopesAndRoles();
            this.populateAssignableScopes();
            if (this.scopesData.length) {
                this.selectedScopeIndex = 0;
                this.selectedRoleIndex = 0;
            }
            this.notifyScopesChanged(false);
        },
        populateAssignedScopesAndRoles() {
            if (this.scopesData.length) {
                // this.assignedScopesAndRoles = [...this.scopesData];
                this.assignedScopesAndRoles = this.scopesData.map(item => {
                    return {
                        ...item,
                        roles: item.roles.map(role => {
                            return { ...role };
                        })
                    };
                });
            }
        },
        populateAssignableScopes() {
            // build the assignable scopes list to feed the 'add scope' dropdown
            let scopes = [];
            const allScopes = this.assignableScopesData.map(item => ({
                mso: item.mso.identifier,
                partner: item.partner.identifier,
                venue: item.venue.identifier
            }));
            allScopes.forEach(item => {
                if (this.canEditScope(item, this.grantableScopesData)) {
                    scopes.push(item);
                }
            });
            // add the scopes from the userInfo
            scopes.push(
                ...this.getGrantableScopes(this.grantableScopesData, allScopes)
            );
            scopes = this.dedupScopeList(scopes);
            this.sortScopeList(scopes);
            this.assignableScopes = scopes;
        },
        populateAssignableRoles(selectedScope) {
            // will get the assignable roles based on the selected scope
            // need to get all roles from selected scope and check if withGrant
            let roles = this.getGrantableRoles(
                selectedScope,
                this.grantableScopesData
            );
            this.assignableRoles = roles;
        },
        notifyScopesChanged(isDirty = true) {
            this.$emit('change', {
                scopes: this.assignedScopesAndRoles,
                isValid: this.isScopesAndRolesValid,
                isDirty
            });
        },
        isScopeSelectionDisabled(scope, xocAuth) {
            // return true if wildcard scope and non MSO user
            if (!xocAuth) {
                return (
                    scope.mso === '*' ||
                    scope.partner === '*' ||
                    scope.venue === '*'
                );
            }
            return false;
        }
    }
};
</script>

<style lang="scss">
.theme--light .add-scope-options-select .v-select__slot .v-text-field__suffix {
    color: map-get($deep-grey, 'darken-1');
}
.theme--dark .add-scope-options-select .v-select__slot .v-text-field__suffix {
    color: map-get($deep-grey, 'lighten-2');
}
.add-scope-options-select .v-select__slot .v-label:not(.v-label--is-disabled) {
    color: var(--v-accent-base);
}
</style>
